import { useRef, useState, useEffect, useCallback, FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import styles from './DayTimePillPopup.module.scss';
import Pill from '@z-components/atoms/Pill';
import Button from 'components/atoms/Button';
import { DAYS_OF_THE_WEEK, TIMES_OF_THE_DAY } from '../PillFilters/constants';

interface Props {
  onDismiss: (show: boolean) => void;
  onSubmit: (values: Array<string>) => void;
  showDay?: boolean;
  showTime?: boolean;
}

const DayTimePillPopup: FC<Props> = ({ onDismiss, onSubmit, showDay, showTime }) => {
  const router = useRouter();

  const { asPath } = router;
  // Add default base URL for Chromatic
  const { searchParams } = new URL(asPath, process.env.DOMAIN ?? 'https://zehitomo.com');
  const dayUrlVal = searchParams.get('day');
  const timeUrlVal = searchParams.get('time');
  const [selectedDay, setSelectedDay] = useState<string>(dayUrlVal ?? '');
  const [selectedTime, setSelectedTime] = useState<string>(timeUrlVal ?? '');

  const container = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const handleContainerClick = useCallback<EventListener>(
    (event) => {
      if (event.target === container.current) {
        onDismiss(false);
      }
    },
    [onDismiss]
  );

  const handleKeyUp = useCallback<EventListener>(
    (event) => {
      if ((event as KeyboardEvent).key === 'Escape') {
        onDismiss(false);
      }
    },
    [onDismiss]
  );

  useEffect(() => {
    const { current } = container;

    if (current) {
      current.addEventListener('click', handleContainerClick);
      window.addEventListener('keyup', handleKeyUp);
    }

    return () => {
      if (current) {
        current.removeEventListener('click', handleContainerClick);
        window.removeEventListener('keyup', handleKeyUp);
      }
    };
  }, [handleContainerClick, handleKeyUp]);

  return createPortal(
    <div className={styles['overlay-container']} ref={container}>
      <div className={styles['content-container']}>
        <div className={styles['close-button-container']}>
          <button
            aria-label="Close"
            className={styles['close-button']}
            onClick={() => onDismiss(false)}
          />
        </div>
        <div className={styles['pills-section-padding']}>
          {showDay && (
            <div className={styles['time-section-title']}>
              <span className={styles['section-title-text']}>
                {t('verticalPages:filters.dayOfWeek')}
              </span>
            </div>
          )}

          {showDay && (
            <div className={styles['pills-container']}>
              {DAYS_OF_THE_WEEK.map((day) => (
                <div className={styles['pill']} key={day}>
                  <Pill
                    id={day}
                    text={t(`common:daysOfTheWeek.${day}`)}
                    selected={day === selectedDay}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => setSelectedDay(day === selectedDay ? '' : day)}
                  />
                </div>
              ))}
            </div>
          )}

          {showDay && showTime && <div className={styles['divider']} />}

          {showTime && (
            <div className={styles['time-section-title']}>
              <span className={styles['section-title-text']}>
                {t('verticalPages:filters.timeOfDay')}
              </span>
            </div>
          )}
          {showTime && (
            <div className={styles['pills-container']}>
              {TIMES_OF_THE_DAY.map((time) => (
                <div className={styles['pill']} key={time}>
                  <Pill
                    id={time}
                    text={t(`common:timesOfDay.${time}`)}
                    selected={time === selectedTime}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => setSelectedTime(time === selectedTime ? '' : time)}
                  />
                </div>
              ))}
            </div>
          )}

          <div className={styles['submit-button-container']}>
            {/* eslint-disable-next-line react/jsx-no-bind */}
            <Button
              fullWidth={true}
              variant="primary"
              onClick={() => onSubmit([selectedDay, selectedTime])}
            >
              {t('verticalPages:filters.search')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DayTimePillPopup;
export type { Props as DayTimePillPopupProps };
